import React from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { formatDateTimeNl } from '../../utils/formatDateTime';
import { signupSourceLabel } from '../../utils/marketingSignup';
import { prefetchCustomer360 } from '../../services/pageDataCache';
import './KlantenWeekAudience.css';

function signalLabel(row) {
  const s = row.behavior_signals || {};
  const parts = [];
  if (s.cart_active) parts.push(`mandje (${s.cart_item_count || '?'})`);
  if (s.product_views) parts.push(`${s.product_views} views`);
  if (s.configurator) parts.push('configurator');
  if (row.behavior_segment) parts.push(row.behavior_segment);
  return parts.length ? parts.join(' · ') : '—';
}

export default function KlantenWeekAudienceTable({
  data,
  loading,
  error,
  onRefresh,
  onOpen360,
  compact = false,
  mode = 'klanten',
}) {
  const marketing = mode === 'marketing';
  const showDetailCols = marketing || !compact;
  if (loading && !data) {
    return <p className="muted">Weekoverzicht laden…</p>;
  }

  return (
    <div
      className={`klanten-week-audience ${compact ? 'klanten-week-audience--compact' : ''} ${
        marketing ? 'klanten-week-audience--marketing' : ''
      }`}
    >
      <div className={`klanten-week-audience-head ${marketing ? 'marketing-popup-section-head' : ''}`}>
        <div>
          {!marketing ? (
            <h3 className="klanten-week-audience-title">Afgelopen {data?.days || 7} dagen</h3>
          ) : null}
          <p className={`muted klanten-week-audience-sub ${marketing ? 'mpp-section-lead' : ''}`}>
            {marketing ? (
              <>
                Afgelopen <strong>{data?.days || 7} dagen</strong>: ingeschreven via pop-up + bekende kopers met
                shop-bezoek.
              </>
            ) : (
              <>Ingeschreven via pop-up + bekende kopers op 4shine</>
            )}
            {data?.counts ? (
              marketing ? (
                <span className="mpp-section-stats block">
                  {data.counts.ingeschreven} ingeschreven · {data.counts.bekend_bezoek} bekend actief · {data.total}{' '}
                  totaal
                </span>
              ) : (
                <>
                  {' '}
                  · <strong>{data.counts.ingeschreven}</strong> nieuw ·{' '}
                  <strong>{data.counts.bekend_bezoek}</strong> bekend · <strong>{data.total}</strong> totaal
                </>
              )
            ) : null}
          </p>
        </div>
        {onRefresh ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh} disabled={loading}>
            <FiRefreshCw /> Vernieuwen
          </button>
        ) : null}
      </div>

      {error ? <p className="marketing-ig-error">{error}</p> : null}

      {!data?.rows?.length ? (
        <p className="muted">
          {marketing
            ? 'Geen rijen in deze periode. Zodra iemand inschrijft of een bekende koper terugkomt op 4shine, verschijnt het hier.'
            : 'Geen klanten in deze periode.'}
        </p>
      ) : (
        <div className={`klanten-week-table-wrap ${marketing ? 'marketing-popup-table-wrap' : ''}`}>
          <table className={`data-table klanten-week-table ${marketing ? 'marketing-popup-audience-table' : ''}`}>
            <thead>
              <tr>
                <th>Type</th>
                {showDetailCols ? <th>{marketing ? 'Laatste activiteit' : 'Activiteit'}</th> : null}
                <th>E-mail</th>
                {showDetailCols ? <th>Visitor</th> : null}
                <th>{marketing ? 'Variant / bezoeken' : 'Info'}</th>
                <th>Gedrag</th>
                <th>360°</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr
                  key={`${row.kind}-${row.visitor_id || row.email}-${row.at}`}
                  onMouseEnter={() => {
                    if (row.email && row.email !== '—') prefetchCustomer360(row.email).catch(() => {});
                  }}
                >
                  <td>
                    <span className={`mpp-audience-pill mpp-audience-pill--${row.kind}`}>
                      {row.kind === 'ingeschreven'
                        ? marketing
                          ? 'Ingeschreven'
                          : 'Inschrijving'
                        : marketing
                          ? 'Bekend bezoek'
                          : 'Bekend'}
                    </span>
                  </td>
                  {showDetailCols ? (
                    <td className="klanten-week-nowrap">
                      {formatDateTimeNl(row.at)}
                    </td>
                  ) : null}
                  <td className="klanten-week-email">{row.email}</td>
                  {showDetailCols ? (
                    <td className="mpp-mono">
                      {row.visitor_id
                        ? `${row.visitor_id.slice(0, marketing ? 16 : 12)}…`
                        : '—'}
                    </td>
                  ) : null}
                  <td>
                    {row.kind === 'ingeschreven' ? (
                      <>
                        {signupSourceLabel(row.signup_source)}
                        {' · '}
                        {row.variant}
                        {row.subscriber_name ? ` · ${row.subscriber_name}` : ''}
                        {row.coupon_code ? ` · ${row.coupon_code}` : ''}
                        {marketing && row.xendy_synced ? (
                          <span className="muted" title="Xendy sync OK">
                            {' '}
                            · Xendy ✓
                          </span>
                        ) : null}
                        {marketing && !row.xendy_synced && row.xendy_sync_error ? (
                          <span className="marketing-ig-error" title={row.xendy_sync_error}>
                            {' '}
                            · Xendy ✗
                          </span>
                        ) : null}
                        {marketing && !row.xendy_synced && !row.xendy_sync_error ? (
                          <span className="muted" title="Nog niet naar Xendy">
                            {' '}
                            · Xendy ?
                          </span>
                        ) : null}
                      </>
                    ) : marketing ? (
                      `${row.visit_count || 0} bezoeken`
                    ) : (
                      `${row.visit_count || 0}× bezoek`
                    )}
                  </td>
                  <td className={`klanten-week-behavior ${marketing ? 'mpp-behavior-cell' : ''}`}>
                    {signalLabel(row)}
                  </td>
                  <td>
                    <div className="klanten-week-actions">
                      {row.email && row.email !== '—' ? (
                        <button type="button" className="klanten360-open-btn" onClick={() => onOpen360(row.email)}>
                          Open
                        </button>
                      ) : (
                        '—'
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
